<template>
    <div class="role-management-container">
      <div class="page-header">
        <h2 class="page-title">角色管理</h2>
      </div>
  
      <!-- 角色列表 -->
      <el-table
        v-loading="loading"
        :data="roleList"
        border
        style="width: 100%">
        <el-table-column
          prop="id"
          label="角色ID"
          width="80">
        </el-table-column>
        <el-table-column
          prop="roleName"
          label="角色名称"
          width="150">
        </el-table-column>
        <el-table-column
          prop="roleCode"
          label="角色编码"
          width="150">
        </el-table-column>
        <el-table-column
          prop="description"
          label="角色描述">
        </el-table-column>
        <el-table-column
          label="操作"
          width="250">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEditRole(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" @click="handleAssignPermissions(scope.row)">分配权限</el-button>
            <el-button size="mini" type="danger" @click="handleDeleteRole(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
  
      <!-- 角色表单对话框 -->
      <el-dialog 
        :title="dialogTitle" 
        :visible.sync="roleDialogVisible"
        width="550px">
        <el-form 
          ref="roleForm" 
          :model="roleForm" 
          :rules="roleRules" 
          label-width="100px">
          <el-form-item label="角色名称" prop="roleName">
            <el-input v-model="roleForm.roleName" placeholder="请输入角色名称"></el-input>
          </el-form-item>
          <el-form-item label="角色编码" prop="roleCode">
            <el-input v-model="roleForm.roleCode" placeholder="请输入角色编码"></el-input>
          </el-form-item>
          <el-form-item label="角色描述" prop="description">
            <el-input 
              v-model="roleForm.description" 
              type="textarea" 
              :rows="3" 
              placeholder="请输入角色描述">
            </el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="roleDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitRoleForm">确定</el-button>
        </div>
      </el-dialog>
  
      <!-- 权限分配对话框 -->
      <el-dialog 
        title="分配权限" 
        :visible.sync="permissionDialogVisible"
        width="600px">
        <div v-if="currentRole.roleName" class="permission-header">
          <span class="role-name">角色: {{ currentRole.roleName }}</span>
        </div>
        <el-tree
          ref="permissionTree"
          :data="permissionTree"
          :props="{
            label: 'permissionName',
            children: 'children'
          }"
          show-checkbox
          node-key="id"
          default-expand-all
          highlight-current>
          <span class="custom-tree-node" slot-scope="{ node, data }">
            <span>{{ node.label }}</span>
            <span v-if="data.description" class="permission-desc">
              ({{ data.description }})
            </span>
          </span>
        </el-tree>
        <div slot="footer" class="dialog-footer">
          <el-button @click="permissionDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveRolePermissions">确定</el-button>
        </div>
      </el-dialog>

      <!-- 用户角色分配对话框 -->
      <user-role-assignment-dialog :visible.sync="userRoleDialogVisible"></user-role-assignment-dialog>
    </div>
  </template>
  
  <script>
  import { 
    getRoleList, 
    getRoleById, 
    createRole, 
    updateRole, 
    deleteRole, 
    getRolePermissions, 
    assignRolePermissions 
  } from '@/api/role'
  import { getAllPermissions } from '@/api/permission'
  import UserRoleAssignmentDialog from '@/components/permission/UserRoleAssignmentDialog'
  
  export default {
    name: 'RoleManagement',
    components: {
      UserRoleAssignmentDialog
    },
    inject: ['registerNavButtons', 'unregisterNavButtons'],
    data() {
      return {
        loading: false,
        roleList: [],
        // 角色对话框
        roleDialogVisible: false,
        dialogTitle: '添加角色',
        isEdit: false,
        roleForm: {
          roleName: '',
          roleCode: '',
          description: ''
        },
        roleRules: {
          roleName: [
            { required: true, message: '请输入角色名称', trigger: 'blur' },
            { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
          ],
          roleCode: [
            { required: true, message: '请输入角色编码', trigger: 'blur' },
            { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
          ]
        },
        // 权限对话框
        permissionDialogVisible: false,
        currentRole: {},
        permissionTree: [],
        rolePermissions: [],
        // 用户角色分配对话框
        userRoleDialogVisible: false,
        // 导航按钮
        navButtons: [
          {
            id: 'add-role',
            text: '添加角色',
            icon: 'el-icon-plus',
            type: 'primary',
            handler: function() {
              this.handleAddRole();
            }.bind(this)
          },
          {
            id: 'assign-user-roles',
            text: '用户角色分配',
            icon: 'el-icon-user',
            type: 'success',
            handler: function() {
              this.openUserRoleDialog();
            }.bind(this)
          }
        ]
      }
    },
    created() {
      this.fetchRoleList()
      this.fetchPermissionTree()
    },
    mounted() {
      // 注册导航按钮
      if (this.registerNavButtons) {
        this.registerNavButtons(this.navButtons);
      }
    },
    beforeDestroy() {
      // 注销导航按钮
      if (this.unregisterNavButtons) {
        this.unregisterNavButtons();
      }
    },
    methods: {
      // 打开用户角色分配对话框
      openUserRoleDialog() {
        this.userRoleDialogVisible = true;
      },
      
      // 获取角色列表
      async fetchRoleList() {
        this.loading = true
        try {
          const response = await getRoleList()
          
          if (response && response.code === 200) {
            this.roleList = response.data || []
          } else {
            this.$message.error(response?.message || '获取角色列表失败')
          }
        } catch (error) {
          console.error('获取角色列表出错:', error)
          this.$message.error('获取角色列表失败')
        } finally {
          this.loading = false
        }
      },
  
      // 获取权限树
      async fetchPermissionTree() {
        try {
          const response = await getAllPermissions()
          
          if (response && response.code === 200) {
            this.permissionTree = response.data || []
          } else {
            this.$message.error(response?.message || '获取权限列表失败')
          }
        } catch (error) {
          console.error('获取权限列表出错:', error)
          this.$message.error('获取权限列表失败')
        }
      },
      
      // 添加角色
      handleAddRole() {
        this.isEdit = false
        this.dialogTitle = '添加角色'
        this.roleForm = {
          roleName: '',
          roleCode: '',
          description: ''
        }
        this.roleDialogVisible = true
        // 下一帧再重置表单校验结果
        this.$nextTick(() => {
          if (this.$refs.roleForm) {
            this.$refs.roleForm.clearValidate()
          }
        })
      },
      
      // 编辑角色
      async handleEditRole(row) {
        this.isEdit = true
        this.dialogTitle = '编辑角色'
        
        try {
          // 获取完整的角色信息
          const response = await getRoleById(row.id)
          
          if (response && response.code === 200) {
            this.roleForm = { ...response.data }
          } else {
            this.$message.error(response?.message || '获取角色详情失败')
            return
          }
        } catch (error) {
          console.error('获取角色详情出错:', error)
          this.$message.error('获取角色详情失败')
          return
        }
        
        this.roleDialogVisible = true
        // 下一帧再重置表单校验结果
        this.$nextTick(() => {
          if (this.$refs.roleForm) {
            this.$refs.roleForm.clearValidate()
          }
        })
      },
      
      // 提交角色表单
      submitRoleForm() {
        if (!this.$refs.roleForm) return
        
        this.$refs.roleForm.validate(async valid => {
          if (valid) {
            try {
              let response
              if (this.isEdit) {
                // 更新角色
                response = await updateRole(this.roleForm.id, this.roleForm)
              } else {
                // 创建角色
                response = await createRole(this.roleForm)
              }
              
              if (response && response.code === 200) {
                this.$message.success(this.isEdit ? '角色更新成功' : '角色创建成功')
                this.roleDialogVisible = false
                this.fetchRoleList()
              } else {
                this.$message.error(response?.message || (this.isEdit ? '角色更新失败' : '角色创建失败'))
              }
            } catch (error) {
              console.error(this.isEdit ? '更新角色出错:' : '创建角色出错:', error)
              this.$message.error(this.isEdit ? '角色更新失败' : '角色创建失败')
            }
          } else {
            return false
          }
        })
      },
      
      // 删除角色
      async handleDeleteRole(row) {
        try {
          await this.$confirm(`确定要删除角色 ${row.roleName} 吗?`, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
          
          const response = await deleteRole(row.id)
          
          if (response && response.code === 200) {
            this.$message.success('删除角色成功')
            this.fetchRoleList()
          } else {
            this.$message.error(response?.message || '删除角色失败')
          }
        } catch (error) {
          if (error !== 'cancel') {
            console.error('删除角色出错:', error)
            this.$message.error('删除角色失败')
          }
        }
      },
      
      // 分配权限
      async handleAssignPermissions(row) {
        this.currentRole = row
        this.permissionDialogVisible = true
        
        try {
          // 获取角色的权限ID列表
          const response = await getRolePermissions(row.id)
          
          if (response && response.code === 200) {
            this.rolePermissions = response.data || []
            
            // 下一帧设置已选权限
            this.$nextTick(() => {
              if (this.$refs.permissionTree) {
                this.$refs.permissionTree.setCheckedKeys(this.rolePermissions)
              }
            })
          } else {
            this.$message.error(response?.message || '获取角色权限失败')
          }
        } catch (error) {
          console.error('获取角色权限出错:', error)
          this.$message.error('获取角色权限失败')
        }
      },
      
      // 保存角色权限
      async saveRolePermissions() {
        if (!this.$refs.permissionTree) return
        
        try {
          // 获取选中的权限ID
          const permissionIds = this.$refs.permissionTree.getCheckedKeys()
          
          const data = {
            roleId: this.currentRole.id,
            permissionIds: permissionIds
          }
          
          const response = await assignRolePermissions(data)
          
          if (response && response.code === 200) {
            this.$message.success('权限分配成功')
            this.permissionDialogVisible = false
          } else {
            this.$message.error(response?.message || '权限分配失败')
          }
        } catch (error) {
          console.error('保存角色权限出错:', error)
          this.$message.error('权限分配失败')
        }
      },
      
      // 格式化日期
      formatDate(dateStr) {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        return date.toLocaleString()
      }
    }
  }
  </script>
  
  <style scoped>
  .role-management-container {
    padding: 20px;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .page-title {
    font-size: 20px;
    color: #303133;
    margin: 0;
  }
  
  .permission-header {
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e6e6e6;
  }
  
  .role-name {
    font-size: 16px;
    font-weight: bold;
  }
  
  .custom-tree-node {
    flex: 1;
    display: flex;
    align-items: center;
    font-size: 14px;
  }
  
  .permission-desc {
    font-size: 12px;
    color: #909399;
    margin-left: 8px;
  }
  </style>
  